import { Component, OnDestroy, OnInit } from '@angular/core';
import { ChartDataService } from '@app/backend/chart-data.service';
import { GenerateChartDataService } from '@app/backend/generate-chart-data.service';
import { ChartDataInterface, DrillDetailsInterface } from "@app/models/chart-data.model";
import * as Highcharts from 'highcharts/highstock';

import { SiteService } from '@app/app-state/site.service';
import { UserService } from '@app/app-state/user.service';
import * as charts from '@utilities/highchart-objectify';
import { Subscription } from 'rxjs';
import { GraphReportComponent } from '@app/gui/report/graph-report/graph-report.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
	selector: 'app-drill-length-and-diameter',
	templateUrl: './drill-length-and-diameter.component.html',
	styleUrls: ['./drill-length-and-diameter.component.scss'],
	standalone: false
})
export class DrillLengthAndDiameterComponent implements OnInit, OnDestroy  {
	public Highcharts: typeof Highcharts = Highcharts; // required

	public chartOptions = [] // required
	private subscriptions: Subscription = new Subscription();

	public loading:boolean;

	private domainId: number;
	private subdomainId: number;
	private siteId: number;
	private startDate: string;
	private endDate: string;
	private selectedTimeline: string;

	private drillAndLength:ChartDataInterface[] = [];
	public visibleDownloadMenuIndex: number | null = null;

	constructor(
		private chartDataService:ChartDataService,
		private siteService: SiteService,
		private generateChartDataService:GenerateChartDataService,
		private userService: UserService,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.subscriptions.add(this.chartDataService.loading$.subscribe(loading => {
			this.loading = loading;
		}));

		this.subscriptions.add(this.chartDataService.drillDetails$.subscribe((res) => {
			this.triggerLoadData(res);
		}));

		this.subscriptions.add(this.siteService.domainId$.subscribe(domainId => {
			this.domainId = domainId;
			if (this.domainId) {
				this.getReport();
			}
		}));

		this.subscriptions.add(this.siteService.subDomainId$.subscribe(subdomainId => {
			this.subdomainId = subdomainId;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.siteId$.subscribe(siteId => {
			this.siteId = siteId;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.startDate$.subscribe(startDate => {
			this.startDate = startDate;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.endDate$.subscribe(endDate => {
			this.endDate = endDate;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.selectedTimeline$.subscribe(selectedTimeline => {
			this.selectedTimeline = selectedTimeline;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.userService.userUnitMeasurement$.subscribe(() => {
			this.triggerLoadData();
		}));
	}

	private triggerLoadData(data? : ChartDataInterface[]) {
		const originalDrillAndBalstData = data || this.generateChartDataService.originalDrillAndBalstData;
		this.drillAndLength = this.generateChartDataService.getFilteredData(
			originalDrillAndBalstData,
			this.domainId,
			this.subdomainId,
			this.siteId,
			this.startDate,
			this.endDate,
			this.selectedTimeline
		);

		this.loadData(this.drillAndLength);
	}

	private async getReport() {
		this.subscriptions.add(this.chartDataService.getCacheDrillDetails().subscribe((res) => {
			this.triggerLoadData(res);
		}));
	}

	private loadData = (chartData: ChartDataInterface[])=> {
		this.drillAndLength = chartData;
		const data:DrillDetailsInterface = this.generateChartDataService.generateChartData(this.drillAndLength);
		for (const key in data?.drillLengthAndDiameter) {
			this.chartOptions = charts.generateChartOptions(data?.drillLengthAndDiameter[key]);
			this.chartDataService.exportedGraphData$.next(data.drillLengthAndDiameter[key]);
		}
	}

	public pdfDownload() {
		this.dialog.open(GraphReportComponent, {
			width: '100vw',
			height: '100vh',
			maxWidth: '100vw',
			panelClass: 'full-screen-dialog',
			disableClose: true
		});
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	chartRefs: Highcharts.Chart[] = [];

	onChartInstance(chart: Highcharts.Chart, index: number): void {
		this.chartRefs[index] = chart;
	}
}
